import type { LetterDeliveryDecisionDraft, LetterDeliveryDecisionRow } from "../../../contract"
import { validateLetterDeliveryDecision } from "../../../_shared/_logic/02-decide-appointment-letter-delivery-payload"

export type LetterDeliveryEstimatePayload = {
  letterDeliveryBatchItemIds: string[]
  automaticDeliveryItemIds: string[]
}

export type LetterDeliveryEstimatePreparation =
  | { status: "loading" }
  | { status: "error"; detail: string }
  | ({ status: "ready" } & LetterDeliveryEstimatePayload)

export const buildLetterDeliveryEstimatePayload = (
  rows: LetterDeliveryDecisionRow[],
  draftFor: (row: LetterDeliveryDecisionRow) => LetterDeliveryDecisionDraft,
  automaticLetterDeliveryAllowed: boolean,
): LetterDeliveryEstimatePreparation => {
  const openRows = rows.filter(
    (row) => row.isOpen && !row.isResolved && row.rowOutcome?.status !== "success",
  )
  const errorRow = openRows.find((row) => row.estimateState.status === "error")

  if (errorRow?.estimateState.status === "error") {
    return { status: "error", detail: errorRow.estimateState.detail }
  }

  if (openRows.some((row) => row.estimateState.status === "loading")) {
    return { status: "loading" }
  }

  const letterDeliveryBatchItemIds: string[] = []
  const automaticDeliveryItemIds: string[] = []
  for (const row of openRows) {
    if (row.estimateState.status !== "ready") {
      return {
        status: "error",
        detail: "Zustelldaten des Terminbriefs sind unvollständig.",
      }
    }

    const draft = draftFor(row)
    const error = validateLetterDeliveryDecision(row, draft, automaticLetterDeliveryAllowed)

    if (error) return { status: "error", detail: error }

    letterDeliveryBatchItemIds.push(row.estimateState.itemId)

    if (draft.deliveryMode === "automatic" && row.estimateState.itemId) {
      automaticDeliveryItemIds.push(row.estimateState.itemId)
    }
  }

  if (new Set(letterDeliveryBatchItemIds).size !== letterDeliveryBatchItemIds.length) {
    return {
      status: "error",
      detail: "Terminbrief-Items sind in der Zustellansicht nicht eindeutig.",
    }
  }

  return {
    status: "ready",
    letterDeliveryBatchItemIds,
    automaticDeliveryItemIds,
  }
}
